import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Users, 
  FileText, 
  BookOpen, 
  MessageSquare,
  Plus,
  ChevronRight,
  CalendarIcon,
  Clock,
  Sparkles,
  Activity,
  Upload,
  X,
  Paperclip,
  Mail,
  ClipboardCheck,
  AlertCircle,
  LogIn,
  CheckCircle2,
  Bell,
  Video,
  CalendarClock
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import { cn } from "@/lib/utils";

const getMeetingCalColors = (meeting, isPending) => {
  if (meeting.meeting_type === 'group') return { bg: 'bg-indigo-50', label: 'text-indigo-500', day: 'text-indigo-700' };
  if (isPending) return { bg: 'bg-amber-50', label: 'text-amber-500', day: 'text-amber-700' };
  return { bg: 'bg-teal-50', label: 'text-teal-500', day: 'text-teal-700' };
};

export default function TeacherDashboard({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [pendingJournals, setPendingJournals] = useState([]);
  const [students, setStudents] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewAssignment, setShowNewAssignment] = useState(false);
  const [prayerSummary, setPrayerSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingCheckIns, setPendingCheckIns] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const fileInputRef = useRef(null);
  
  // New assignment form
  const [newAssignment, setNewAssignment] = useState({
    title: '',
    description: '',
    scripture_reference: '',
    due_date: null,
    assigned_to: 'all', // 'all' or specific user_id
    attachments: []
  });

  const formatTimeAgo = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const sendReminder = async (studentId, e) => {
    e.stopPropagation();
    setSendingReminder(studentId);
    try {
      const response = await axios.post(`${API}/checkins/remind/${studentId}`);
      toast.success(response.data.message);
      // Refresh data to get updated reminder timestamp
      fetchDashboardData();
    } catch (error) {
      console.error("Error sending reminder:", error);
      toast.error("Failed to send reminder");
    } finally {
      setSendingReminder(null);
    }
  };

  const fetchUnreadMessages = useCallback(async () => {
    try {
      // Get direct messages
      const directRes = await axios.get(`${API}/messages/unread?limit=10`);
      const directMsgs = (directRes.data || []).map(msg => ({...msg, is_group: false}));
      
      // Get group messages
      let groupMsgs = [];
      try {
        const groupRes = await axios.get(`${API}/messages/unread-all`);
        groupMsgs = groupRes.data.group_messages || [];
      } catch (e) {
        console.error("Failed to load group messages:", e);
      }
      
      // Combine and sort
      const allMessages = [...directMsgs, ...groupMsgs].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setUnreadMessages(allMessages);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, activitiesRes, submissionsRes, studentsRes, pendingRes, pendingJournalsRes, pendingCheckInsRes, meetingsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/activities?limit=20`),
        axios.get(`${API}/submissions`),
        axios.get(`${API}/users/students`),
        axios.get(`${API}/submissions/pending?limit=10`),
        axios.get(`${API}/journals/pending?limit=10`),
        axios.get(`${API}/checkins/pending-students`),
        axios.get(`${API}/meetings?time_filter=upcoming`)
      ]);

      setStats(statsRes.data);
      setActivities(activitiesRes.data);
      setSubmissions(submissionsRes.data.filter(s => s.status === 'submitted').slice(0, 5));
      setStudents(studentsRes.data);
      setPendingReviews(pendingRes.data);
      setPendingJournals(pendingJournalsRes.data);
      setPendingCheckIns(pendingCheckInsRes.data);
      setUpcomingMeetings(
        (meetingsRes.data || [])
          .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
          .slice(0, 3)
      );
      
      // Fetch unread messages
      await fetchUnreadMessages();
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  }, [fetchUnreadMessages]);

  useEffect(() => {
    fetchDashboardData();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadedFiles = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        uploadedFiles.push({
          file_id: response.data.file_id,
          filename: response.data.filename,
          size: response.data.size
        });
      }

      setNewAssignment(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedFiles]
      }));
      toast.success(`${uploadedFiles.length} file(s) uploaded`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload file(s)");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachment = (index) => {
    setNewAssignment(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const createAssignment = async () => {
    if (!newAssignment.title.trim()) {
      toast.error("Please enter an assignment title");
      return;
    }

    try {
      await axios.post(`${API}/assignments`, {
        title: newAssignment.title,
        description: newAssignment.description,
        scripture_reference: newAssignment.scripture_reference || null,
        due_date: newAssignment.due_date?.toISOString(),
        assigned_to: newAssignment.assigned_to === 'all' ? null : newAssignment.assigned_to,
        attachments: newAssignment.attachments.map(a => a.file_id)
      });

      toast.success("Assignment created successfully");
      setShowNewAssignment(false);
      setNewAssignment({ 
        title: '', 
        description: '', 
        scripture_reference: '',
        due_date: null,
        assigned_to: 'all',
        attachments: []
      });
      fetchDashboardData();
    } catch (error) {
      console.error("Error creating assignment:", error);
      toast.error("Failed to create assignment");
    }
  };

  const getPrayerSummary = async () => {
    setSummaryLoading(true);
    try {
      const response = await axios.post(`${API}/ai/prayer-summary`);
      setPrayerSummary(response.data.summary);
    } catch (error) {
      console.error("Error getting prayer summary:", error);
      toast.error("Failed to get prayer summary");
    } finally {
      setSummaryLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'assignment_submitted':
        return <FileText className="w-4 h-4 text-blue-600" />;
      case 'journal_submitted':
        return <BookOpen className="w-4 h-4 text-amber-600" />;
      case 'journal_reviewed':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'forum_post':
      case 'forum_comment':
        return <MessageSquare className="w-4 h-4 text-teal-600" />;
      case 'user_login':
        return <LogIn className="w-4 h-4 text-purple-600" />;
      default:
        return <Activity className="w-4 h-4 text-slate-600" />;
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div data-testid="teacher-dashboard" className="space-y-8">
        {/* Unread Messages Alert - Only show when there are messages */}
        {unreadMessages.length > 0 && (
          <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 overflow-hidden">
            <CardContent className="p-0">
              <div className="flex items-stretch">
                {/* Left indicator */}
                <div className="w-2 bg-blue-500 animate-pulse-soft"></div>
                
                <div className="flex-1 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Mail className="w-5 h-5 text-blue-600" />
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                          {unreadMessages.length}
                        </span>
                      </div>
                      <h3 className="font-semibold text-blue-900">New Messages</h3>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate('/messages')}
                      className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      View All
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    {unreadMessages.slice(0, 3).map((msg) => (
                      <div 
                        key={msg.message_id || `${msg.group_id}-${msg.created_at}`}
                        className="flex items-center gap-3 p-3 bg-white/60 rounded-xl cursor-pointer hover:bg-white transition-colors"
                        onClick={() => navigate(msg.is_group ? '/messages' : `/messages/${msg.sender_id}`)}
                      >
                        {msg.is_group ? (
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Users className="w-5 h-5 text-blue-600" />
                          </div>
                        ) : (
                          <Avatar className="w-10 h-10 flex-shrink-0">
                            <AvatarImage src={msg.sender_picture} />
                            <AvatarFallback className="bg-blue-100 text-blue-700">
                              {getInitials(msg.sender_name)}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 text-sm">
                            {msg.is_group ? msg.group_name : msg.sender_name}
                          </p>
                          <p className="text-slate-600 text-sm truncate">
                            {msg.is_group ? `${msg.sender_name}: ` : ''}{msg.content || 'Attachment'}
                          </p>
                        </div>
                        <span className="text-xs text-slate-500 flex-shrink-0">
                          {format(new Date(msg.created_at), 'h:mm a')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
              Teacher Dashboard
            </h1>
            <p className="text-slate-600 mt-1">
              Monitor your students&apos; progress and engagement.
            </p>
          </div>
          
          <Dialog open={showNewAssignment} onOpenChange={setShowNewAssignment}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-assignment-btn"
                className="bg-slate-800 hover:bg-slate-900 rounded-full shadow-lg shadow-slate-800/20"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Assignment
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif">Create New Assignment</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                {/* Title */}
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input 
                    id="title"
                    data-testid="assignment-title-input"
                    value={newAssignment.title}
                    onChange={(e) => setNewAssignment({...newAssignment, title: e.target.value})}
                    placeholder="Assignment title"
                    className="mt-1"
                  />
                </div>

                {/* Scripture Reference */}
                <div>
                  <Label htmlFor="scripture">Scripture Reference</Label>
                  <Input 
                    id="scripture"
                    data-testid="scripture-reference-input"
                    value={newAssignment.scripture_reference}
                    onChange={(e) => setNewAssignment({...newAssignment, scripture_reference: e.target.value})}
                    placeholder="e.g., John 3:16 or Romans 8:28-30"
                    className="mt-1"
                  />
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea 
                    id="description"
                    data-testid="assignment-description-input"
                    value={newAssignment.description}
                    onChange={(e) => setNewAssignment({...newAssignment, description: e.target.value})}
                    placeholder="Describe the assignment..."
                    className="mt-1 min-h-[100px]"
                  />
                </div>

                {/* Assign To */}
                <div>
                  <Label>Assign To</Label>
                  <Select 
                    value={newAssignment.assigned_to} 
                    onValueChange={(v) => setNewAssignment({...newAssignment, assigned_to: v})}
                  >
                    <SelectTrigger className="mt-1" data-testid="assign-to-select">
                      <SelectValue placeholder="Select recipient" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-2" />
                          Whole Class
                        </div>
                      </SelectItem>
                      {students.map((student) => (
                        <SelectItem key={student.user_id} value={student.user_id}>
                          {student.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Due Date */}
                <div>
                  <Label>Due Date (Optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal mt-1",
                          !newAssignment.due_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {newAssignment.due_date ? format(newAssignment.due_date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={newAssignment.due_date}
                        onSelect={(date) => setNewAssignment({...newAssignment, due_date: date})}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* File Upload */}
                <div>
                  <Label>Attachments</Label>
                  <div className="mt-1 space-y-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full"
                    >
                      {uploading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mr-2"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Documents
                        </>
                      )}
                    </Button>
                    
                    {/* Attached Files List */}
                    {newAssignment.attachments.length > 0 && (
                      <div className="space-y-2">
                        {newAssignment.attachments.map((file, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Paperclip className="w-4 h-4 text-slate-500 flex-shrink-0" />
                              <span className="text-sm text-slate-700 truncate">{file.filename}</span>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAttachment(index)}
                              className="h-8 w-8 p-0 flex-shrink-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <Button 
                  data-testid="submit-assignment-btn"
                  onClick={createAssignment} 
                  className="w-full bg-slate-800 hover:bg-slate-900 rounded-full"
                >
                  Create Assignment
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Grid - Control Room Layout */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className="border-slate-100 card-hover cursor-pointer transition-all hover:shadow-md"
            onClick={() => navigate('/students')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Students</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_students || 0}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-slate-100 card-hover cursor-pointer transition-all hover:shadow-md"
            onClick={() => navigate('/assignments')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Assignments</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_assignments || 0}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-amber-200 bg-amber-50/50 card-hover cursor-pointer transition-all hover:shadow-md"
            onClick={() => navigate('/assignments')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-700">Pending Reviews</p>
                  <p className="text-3xl font-bold text-amber-800 mt-1">{stats?.pending_submissions || 0}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-teal-200 bg-teal-50/50 card-hover cursor-pointer transition-all hover:shadow-md"
            onClick={() => navigate('/journals')}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-teal-700">Pending Journals</p>
                  <p className="text-3xl font-bold text-teal-800 mt-1">{stats?.pending_journals || 0}</p>
                </div>
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Meetings – Calendar Style */}
        <div data-testid="teacher-upcoming-meetings-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-lg font-bold text-slate-900">Upcoming Meetings</h2>
            <div className="flex items-center gap-2">
              <Button
                data-testid="teacher-schedule-meeting-btn"
                size="sm"
                className="rounded-full bg-teal-600 hover:bg-teal-700 text-white shadow-md shadow-teal-600/20"
                onClick={() => navigate('/meetings')}
              >
                <CalendarClock className="w-4 h-4 mr-1.5" />
                Manage Meetings
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/meetings')}
                className="text-slate-500 hover:text-slate-700 rounded-full"
              >
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
          {upcomingMeetings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingMeetings.map((meeting) => {
                const dt = meeting.scheduled_at ? new Date(meeting.scheduled_at) : null;
                const isPending = meeting.status === 'pending_approval';
                const calColors = getMeetingCalColors(meeting, isPending);
                return (
                  <div
                    key={meeting.meeting_id}
                    data-testid={`teacher-meeting-${meeting.meeting_id}`}
                    className={`group bg-white border rounded-2xl overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 ${meeting.meeting_type === 'group' ? 'border-indigo-200 hover:border-indigo-400 ring-1 ring-indigo-100' : 'border-slate-200 hover:border-teal-300'}`}
                    onClick={() => navigate(`/meetings/${meeting.meeting_id}`)}
                  >
                    {meeting.meeting_type === 'group' && (
                      <div className="bg-indigo-600 px-4 py-1 flex items-center gap-1.5">
                        <Users className="w-3 h-3 text-indigo-200" />
                        <span className="text-[10px] font-bold text-white uppercase tracking-wider">Group Class</span>
                      </div>
                    )}
                    <div className="flex h-full">
                      {/* Calendar Icon Block */}
                      {dt && (
                        <div className={`w-20 shrink-0 flex flex-col items-center justify-center text-center ${calColors.bg}`}>
                          <span className={`text-[11px] font-bold uppercase tracking-wider ${calColors.label}`}>
                            {format(dt, 'EEE')}
                          </span>
                          <span className={`text-3xl font-extrabold leading-none mt-0.5 ${calColors.day}`}>
                            {format(dt, 'd')}
                          </span>
                          <span className={`text-[11px] font-semibold ${calColors.label}`}>
                            {format(dt, 'MMM')}
                          </span>
                        </div>
                      )}
                      {/* Meeting Info */}
                      <div className="flex-1 p-4 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-semibold text-slate-900 truncate">{meeting.title}</p>
                          {isPending && (
                            <Badge variant="outline" className="text-[10px] h-4 shrink-0 bg-amber-50 text-amber-600 border-amber-200">
                              Pending
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                          with {meeting.student_name || 'Student'}
                        </p>
                        {dt && (
                          <p className="text-lg font-bold text-slate-800 mt-2 leading-tight">
                            {format(dt, 'h:mm a')}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                          {meeting.duration_minutes && (
                            <span className="text-[11px] text-slate-400">{meeting.duration_minutes} min</span>
                          )}
                          {meeting.zoom_link && (
                            <span className="flex items-center gap-0.5 text-[11px] text-blue-500">
                              <Video className="w-3 h-3" />
                              Zoom
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center">
              <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <CalendarClock className="w-7 h-7 text-teal-400" />
              </div>
              <p className="text-sm text-slate-500">No upcoming meetings</p>
              <Button
                data-testid="teacher-go-to-meetings-btn"
                size="sm"
                className="mt-3 rounded-full bg-teal-600 hover:bg-teal-700 text-white"
                onClick={() => navigate('/meetings')}
              >
                View Meetings
              </Button>
            </div>
          )}
        </div>

        {/* Pending Assignment Reviews Panel */}
        {pendingReviews.length > 0 && (
          <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-amber-900">
                      Assignments Awaiting Review
                    </CardTitle>
                    <CardDescription className="text-amber-700">
                      {pendingReviews.length} submission{pendingReviews.length !== 1 ? 's' : ''} need{pendingReviews.length === 1 ? 's' : ''} your feedback
                    </CardDescription>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/assignments')}
                  className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-100"
                >
                  View All
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {pendingReviews.slice(0, 6).map((review) => (
                  <div 
                    key={review.submission_id}
                    className="flex items-center gap-3 p-3 bg-white/70 rounded-xl cursor-pointer hover:bg-white hover:shadow-sm transition-all"
                    onClick={() => navigate(`/assignments/${review.assignment_id}`)}
                  >
                    <Avatar className="w-10 h-10 flex-shrink-0">
                      <AvatarImage src={review.student_picture} />
                      <AvatarFallback className="bg-amber-100 text-amber-700">
                        {getInitials(review.student_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm truncate">{review.student_name}</p>
                      <p className="text-xs text-slate-600 truncate">{review.assignment_title}</p>
                      <p className="text-xs text-amber-600 mt-0.5">
                        {format(new Date(review.created_at), 'MMM d, h:mm a')}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Journals Panel */}
        {pendingJournals.length > 0 && (
          <Card className="border-teal-200 bg-gradient-to-r from-teal-50 to-cyan-50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-teal-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-teal-900">
                      Journals Awaiting Review
                    </CardTitle>
                    <CardDescription className="text-teal-700">
                      {pendingJournals.length} journal{pendingJournals.length !== 1 ? 's' : ''} need{pendingJournals.length === 1 ? 's' : ''} your feedback
                    </CardDescription>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/journals')}
                  className="rounded-full border-teal-300 text-teal-700 hover:bg-teal-100"
                >
                  View All
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {pendingJournals.slice(0, 6).map((journal) => (
                  <div 
                    key={journal.journal_id}
                    className="flex items-center gap-3 p-3 bg-white/70 rounded-xl cursor-pointer hover:bg-white hover:shadow-sm transition-all"
                    onClick={() => navigate(`/journals/${journal.journal_id}`)}
                  >
                    <Avatar className="w-10 h-10 flex-shrink-0">
                      <AvatarImage src={journal.student_picture} />
                      <AvatarFallback className="bg-teal-100 text-teal-700">
                        {getInitials(journal.student_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm truncate">{journal.student_name}</p>
                      <p className="text-xs text-slate-600 truncate">{journal.title}</p>
                      <p className="text-xs text-teal-600 mt-0.5">
                        {format(new Date(journal.created_at), 'MMM d, h:mm a')}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending Weekly Check-Ins Panel */}
        {pendingCheckIns && pendingCheckIns.pending_count > 0 && (
          <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <ClipboardCheck className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-blue-900">
                      Pending Weekly Check-Ins
                    </CardTitle>
                    <CardDescription className="text-blue-700">
                      {pendingCheckIns.pending_count} student{pendingCheckIns.pending_count !== 1 ? 's haven\'t' : ' hasn\'t'} submitted this week
                    </CardDescription>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/checkins')}
                  className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  View All Check-Ins
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {pendingCheckIns.pending_students?.slice(0, 6).map((student) => (
                  <div 
                    key={student.user_id}
                    className="flex items-center gap-3 p-3 bg-white/70 rounded-xl"
                  >
                    <Avatar className="w-10 h-10 flex-shrink-0">
                      <AvatarImage src={student.picture} />
                      <AvatarFallback className="bg-blue-100 text-blue-700">
                        {getInitials(student.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm truncate">{student.name}</p>
                      {student.last_reminded_at ? (
                        <div className="flex items-center gap-1 mt-0.5">
                          <CheckCircle2 className="w-3 h-3 text-green-500" />
                          <p className="text-xs text-green-600">Notified {formatTimeAgo(student.last_reminded_at)}</p>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 mt-0.5">
                          <AlertCircle className="w-3 h-3 text-amber-500" />
                          <p className="text-xs text-amber-600">No check-in this week</p>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={student.last_reminded_at 
                        ? "text-green-600 hover:text-green-700 hover:bg-green-100 flex-shrink-0"
                        : "text-blue-600 hover:text-blue-700 hover:bg-blue-100 flex-shrink-0"
                      }
                      onClick={(e) => sendReminder(student.user_id, e)}
                      disabled={sendingReminder === student.user_id}
                    >
                      {sendingReminder === student.user_id ? (
                        <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                      ) : (
                        <>
                          <Bell className="w-4 h-4 mr-1" />
                          {student.last_reminded_at ? 'Again' : 'Remind'}
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Activity Feed */}
          <Card className="lg:col-span-2 border-slate-100 flex flex-col">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center">
                <Activity className="w-5 h-5 mr-2 text-slate-600" />
                Activity Feed
              </CardTitle>
              <CardDescription>Real-time updates from your students</CardDescription>
            </CardHeader>
            <CardContent className="flex-1">
              <ScrollArea className="h-full min-h-[400px] pr-4">
                {activities.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No recent activity</p>
                ) : (
                  <div className="space-y-4">
                    {activities.map((activity) => (
                      <div 
                        key={activity.activity_id}
                        className="flex items-start gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                      >
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                          {getActivityIcon(activity.action_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-900">
                            <span className="font-medium">{activity.user_name}</span>
                            {' '}{activity.description}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {format(new Date(activity.created_at), 'MMM d, h:mm a')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pending Submissions */}
            <Card className="border-slate-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">Recent Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                {submissions.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-4">No pending submissions</p>
                ) : (
                  <div className="space-y-2">
                    {submissions.map((sub) => (
                      <div 
                        key={sub.submission_id}
                        className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => navigate(`/assignments/${sub.assignment_id}`)}
                      >
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {sub.content?.substring(0, 50)}...
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {format(new Date(sub.created_at), 'MMM d, h:mm a')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                <Button 
                  variant="link" 
                  className="w-full mt-3 text-slate-600"
                  onClick={() => navigate('/assignments')}
                >
                  View All Assignments <ChevronRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Prayer Summary AI */}
            <Card className="border-amber-200 bg-gradient-to-br from-amber-50/50 to-orange-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center">
                  <Sparkles className="w-4 h-4 mr-2 text-amber-600" />
                  AI Prayer Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                {prayerSummary ? (
                  <div className="text-sm text-slate-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {prayerSummary}
                  </div>
                ) : (
                  <p className="text-sm text-slate-600 mb-3">
                    Get an AI-generated summary of recent prayer requests to better support your students.
                  </p>
                )}
                <Button 
                  data-testid="prayer-summary-btn"
                  onClick={getPrayerSummary}
                  disabled={summaryLoading}
                  className="w-full mt-3 bg-amber-600 hover:bg-amber-700 rounded-full"
                >
                  {summaryLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {prayerSummary ? 'Refresh Summary' : 'Generate Summary'}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card className="border-slate-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/journals')}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Review Journals
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/forum')}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Post Announcement
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/messages')}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Message Students
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
